import { ref, computed, watch } from 'vue'
import { settingsService } from '@/services/api'

// Global state for site style
const siteStyle = ref<'magazine' | 'eshop' | 'tours' | 'restaurant'>('tours') // Default to tours
const isLoading = ref(false)
const isLoaded = ref(false)
const navigationSettings = ref<any>(null)

// Page Header Settings - using same keys as NavigationSettings.vue
const pageHeaderSettings = ref<{
  nav_header_gradient_start?: string
  nav_header_gradient_end?: string
  nav_header_title_size?: string
}>({})

// Load site style from backend
const loadSiteStyle = async () => {
  if (isLoaded.value && !isLoading.value) return // Already loaded

  try {
    isLoading.value = true
    const response = await settingsService.getSettings()
    
    if (response.success && response.settings) {
      const style = response.settings.site_style
      if (style === 'magazine' || style === 'eshop' || style === 'tours' || style === 'restaurant') {
        siteStyle.value = style
      }
    }
    
    isLoaded.value = true
  } catch (error) {
    // Keep default value on error
  } finally {
    isLoading.value = false
  }
}

// Load navigation settings including page header styles
const loadNavigationSettings = async () => {
  try {
    const response = await settingsService.getNavigationSettings()
    
    if (response.success && response.data) {
      navigationSettings.value = response.data
      
      // Load page header settings if present
      if (response.data.pageHeader) {
        pageHeaderSettings.value = response.data.pageHeader
        applyCssVariables()
      }
    }
  } catch (error) {
    console.error('Failed to load navigation settings:', error)
  }
}

// Computed properties for conditional display
const showCart = computed(() => siteStyle.value === 'eshop')
const showWishlist = computed(() => siteStyle.value !== 'magazine' && siteStyle.value !== 'restaurant') // Hide wishlist in magazine and restaurant mode
const isMagazineMode = computed(() => siteStyle.value === 'magazine')
const isEshopMode = computed(() => siteStyle.value === 'eshop')
const isToursMode = computed(() => siteStyle.value === 'tours')
const isRestaurantMode = computed(() => siteStyle.value === 'restaurant')

// Function to update site style (for admin use)
const updateSiteStyle = async (newStyle: 'magazine' | 'eshop' | 'tours' | 'restaurant') => {
  try {
    isLoading.value = true
    const response = await settingsService.updateSettings({
      site_style: newStyle
    })
    
    if (response.success) {
      siteStyle.value = newStyle
      return true
    } else {
      return false
    }
  } catch (error) {
    return false
  } finally {
    isLoading.value = false
  }
}

// Apply CSS variables to document root when values change
const applyCssVariables = () => {
  try {
    const root = document.documentElement
    const settings = pageHeaderSettings.value
    
    if (settings.nav_header_gradient_start) {
      root.style.setProperty('--page-header-gradient-start', settings.nav_header_gradient_start)
    }
    if (settings.nav_header_gradient_end) {
      root.style.setProperty('--page-header-gradient-end', settings.nav_header_gradient_end)
    }
    if (settings.nav_header_title_size) {
      root.style.setProperty('--page-header-title-size', settings.nav_header_title_size)
    }
  } catch (e) {
    // noop in SSR or environments without document
  }
}

// Watch for changes to pageHeaderSettings and apply CSS variables
if (typeof window !== 'undefined') {
  watch(pageHeaderSettings, () => {
    applyCssVariables()
  }, { deep: true })
}

export const useSiteStyle = () => {
  // Auto-load on first use if not already loaded
  if (!isLoaded.value && !isLoading.value) {
    loadSiteStyle()
    loadNavigationSettings()
  }

  return {
    // State
    siteStyle: computed(() => siteStyle.value),
    isLoading: computed(() => isLoading.value),
    isLoaded: computed(() => isLoaded.value),
    navigationSettings: computed(() => navigationSettings.value),
    pageHeaderSettings: computed(() => pageHeaderSettings.value),
    
    // Computed properties
    showCart,
    showWishlist,
    isMagazineMode,
    isEshopMode,
    isToursMode,
    isRestaurantMode,
    
    // Actions
    loadSiteStyle,
    loadNavigationSettings,
    updateSiteStyle,
    applyCssVariables
  }
}
